import React, { useRef, useState } from "react";
import { Upload, Loader2, X, Plus, AlignLeft, AlignCenter, AlignRight, AlignJustify } from "lucide-react";
import api from "../../lib/api";

export const inputCls = "w-full rounded-lg border border-line bg-white px-3.5 py-2.5 text-sm outline-none focus:border-gold";
export const labelCls = "block text-xs font-semibold text-ink mb-1.5";

export function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

export function Input({ label, value, onChange, ...props }) {
  return <Field label={label}><input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...props} /></Field>;
}

export function AlignPicker({ value = "left", onChange }) {
  const opts = [["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight], ["justify", AlignJustify]];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-md border border-line p-0.5" data-testid="align-picker">
      {opts.map(([v, Icon]) => (
        <button type="button" key={v} onClick={() => onChange(v)} title={`Align ${v}`} data-testid={`align-${v}`}
          className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${(value || "left") === v ? "bg-gold text-white" : "text-muted hover:text-ink"}`}>
          <Icon size={14} />
        </button>
      ))}
    </div>
  );
}

export function Textarea({ label, value, onChange, rows = 3, align, onAlign }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <label className="text-xs font-semibold text-ink">{label}</label>
        {onAlign && <AlignPicker value={align} onChange={onAlign} />}
      </div>
      <textarea className={inputCls} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Select({ label, value, onChange, options }) {
  return <Field label={label}><select className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></Field>;
}

export function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg border border-line bg-white px-3.5 py-2.5 cursor-pointer">
      <span className="text-sm font-medium text-ink">{label}</span>
      <button type="button" onClick={() => onChange(!checked)} className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-gold" : "bg-line"}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-[90] bg-ink/50 flex items-start justify-center p-3 overflow-y-auto" onClick={onClose}>
      <div className={`bg-white rounded-xl2 w-full ${wide ? "max-w-4xl" : "max-w-lg"} my-6`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-line sticky top-0 bg-white rounded-t-xl2 z-10">
          <h3 className="font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-sand flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

// Upload image to Cloudinary via signed flow; falls back to URL paste.
async function uploadToCloudinary(file, folder) {
  const { data: sig } = await api.get(`/cloudinary/signature?folder=${encodeURIComponent(folder)}`);
  const fd = new FormData();
  fd.append("file", file);
  fd.append("api_key", sig.api_key);
  fd.append("timestamp", sig.timestamp);
  fd.append("signature", sig.signature);
  fd.append("folder", sig.folder);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, { method: "POST", body: fd });
  const json = await res.json();
  if (!json.secure_url) throw new Error(json.error?.message || "Upload failed");
  return json.secure_url;
}

export function ImageField({ label, value, onChange, folder = "gnons/uploads/" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try { onChange(await uploadToCloudinary(file, folder)); }
    catch (e2) { setErr("Cloudinary not configured. Paste an image URL instead."); }
    finally { setBusy(false); }
  };
  return (
    <Field label={label}>
      <div className="flex gap-3 items-start">
        <div className="relative w-20 h-20 rounded-lg border border-line overflow-hidden bg-sand shrink-0">
          {value ? <img src={value} alt="" className="img-cover" /> : <span className="text-[10px] text-muted flex items-center justify-center h-full">No image</span>}
        </div>
        <div className="flex-1">
          <input className={inputCls} placeholder="Paste image URL" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
          <div className="flex items-center gap-2 mt-2">
            <button type="button" onClick={() => ref.current.click()} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold border border-line rounded-lg px-3 py-1.5 hover:border-gold">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
            </button>
            <input ref={ref} type="file" accept="image/*" className="hidden" onChange={doUpload} />
          </div>
          {err && <p className="text-[11px] text-amber-600 mt-1">{err}</p>}
        </div>
      </div>
    </Field>
  );
}

export function ListEditor({ label, items, onChange, placeholder = "Add item" }) {
  const [val, setVal] = useState("");
  return (
    <Field label={label}>
      <div className="flex gap-2 flex-wrap mb-2">
        {(items || []).map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-beige text-ink text-xs rounded-full px-3 py-1.5">
            {it}<button type="button" onClick={() => onChange(items.filter((_, k) => k !== i))}><X size={12} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input className={inputCls} value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (val.trim()) { onChange([...(items || []), val.trim()]); setVal(""); } } }} />
        <button type="button" onClick={() => { if (val.trim()) { onChange([...(items || []), val.trim()]); setVal(""); } }} className="px-3 rounded-lg bg-ink text-white"><Plus size={16} /></button>
      </div>
    </Field>
  );
}

export function Btn({ children, onClick, variant = "primary", type = "button", ...props }) {
  const styles = { primary: "bg-gold text-white hover:bg-gold-dark", dark: "bg-ink text-white hover:bg-black", outline: "border border-line text-ink hover:border-gold", danger: "bg-red-500 text-white hover:bg-red-600" };
  return <button type={type} onClick={onClick} className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${styles[variant]}`} {...props}>{children}</button>;
}
